// @flow
import styles from "./Add.scss";

import moment from "moment";
import React, { useState, useEffect, useRef } from "react";

import get from "lodash.get";
import { DateTimeField } from "../DatePickers";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import FormHelperText from "@material-ui/core/FormHelperText";
import CircularProgress from "@material-ui/core/CircularProgress";
import Alert from "@material-ui/lab/Alert";

import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import isEqual from "lodash.isequal";

import { type State as VenueState } from "../../redux/modules/venues.d";
import { type State as OrganisersState } from "../../redux/modules/organisers.d";
import { type State as EventDetails } from "../../redux/modules/event-details.d";
import AsyncSelect from "../AsyncSelect";
import AsyncFilteredSelect from "../AsyncFilteredSelect";
import VenueBox from "../VenueBox/VenueBox";
import SelectStatus from "../SelectStatus";
import SelectList from "./DefineEvents/SelectList/SelectList";

type Props = {
    match: *,
    translate: *,
    onClose: () => void,
    /** submit */
    onSubmit: (
        show: *,
        internal: ?string,
        seriesName: ?string,
        serieUuid: ?string,
        optionCode: ?string,
        category: ?string,
    ) => *,
    /** Load event details */
    loadEventDetails: (uuid: string, isAddToSeriesEvent?: boolean) => *,
    events: *,
    /** Find events */
    findEvents: (filter: string) => *,
    /** Disables the form */
    disabled: boolean,
    /**venues */
    venues: VenueState,
    /** Load venues */
    loadVenues: () => any,
    /** Load Organisers */
    loadOrganisers: *,
    /**  Organisers state */
    organisers: OrganisersState,
    id: string,
    addPromoter: (value: *) => Promise<*>,
    addPromoterState: *,
    addSerieState: *,
    addSerie: *,
    addShowState: *,
    addEventState: *,
    /**eventDetails */
    eventDetails: EventDetails,
    refetchOrganisers: () => Promise<*>,
    loadCategories: (uuid: string) => *,
    categories: *,
};

/**
 * Add dialog
 */
const Add = ({
    match,
    translate,
    onClose,
    onSubmit,
    loadEventDetails,
    events,
    findEvents,
    disabled: disabledProp,
    venues,
    loadVenues,
    loadOrganisers,
    organisers,
    id,
    addPromoterState,
    addSerieState,
    addShowState,
    addEventState,
    eventDetails,
    loadCategories,
    categories,
}: Props) => {
    /**
     * Get Series uuid if Add option to series has been clicked
     * Only considered valid if length > 13
     */
    const uuid = (() => {
        const paramUuid = match.params.uuid ? match.params.uuid.toString() : "";
        return paramUuid ? (paramUuid.length > 13 ? paramUuid : "") : "";
    })();

    /**
     * Compute initial start/end from route date param
     */
    const getInitialDates = () => {
        const paramDate = match.params.date;
        const dateStr = paramDate ? paramDate.slice(0, -3).toString() : null;
        if (dateStr) {
            return {
                start: moment.utc(dateStr),
                end: moment.utc(dateStr).clone().add(2, "hours"),
            };
        }
        return {
            start: moment().add(1, "days"),
            end: moment().add(1, "days").add(2, "hours"),
        };
    };

    const initialDates = getInitialDates();

    const [name, setName] = useState(null);
    const [seriesName, setSeriesName] = useState(null);
    const [serieUuid, setSerieUuid] = useState(null);
    const [promoterUuid, setPromoterUuid] = useState(null);
    const [assignParking] = useState(false);
    const [start, setStart] = useState(initialDates.start);
    const [end, setEnd] = useState(initialDates.end);
    const [venueUuid, setVenueUuid] = useState("");
    const [status, setStatus] = useState(null);
    const [internal, setInternal] = useState("");
    const [optionCode, setOptionCode] = useState(null);
    const [showAlert, setShowAlert] = useState(false);
    const [category, setCategory] = useState(null);

    const prevEventDetailsDataRef = useRef(null);

    const disabled = !!(disabledProp || (addShowState && addShowState.loading));

    const eventDetailsData =
        !eventDetails.loading && eventDetails.data ? eventDetails.data : null;

    const venueCode = (() => {
        if (venues.data && venueUuid) {
            const selectedVenue = venues.data.filter(
                el => el.uuid === venueUuid,
            );
            if (selectedVenue.length > 0) {
                return selectedVenue[0].code.toString();
            }
        }
        return null;
    })();

    const defaultPromoter =
        promoterUuid &&
        organisers.data &&
        organisers.data.find(p => p.uuid === promoterUuid);

    /**
     * Get errors
     */
    const getError = (key: string) => {
        let transformedKey = key;

        if (serieUuid && addShowState.fieldErrors) {
            if (key === "serie") return;
            if (key === "internal") return;
            return get(addShowState.fieldErrors, transformedKey);
        }

        if (addSerieState.fieldErrors) {
            if (key === "serie") transformedKey = "name";
            return (
                get(addSerieState.fieldErrors, transformedKey) ||
                get(
                    addSerieState.fieldErrors,
                    `createEventRequests[0].${transformedKey}`,
                )
            );
        }

        if (addEventState.fieldErrors) {
            if (key === "serie") {
                transformedKey = "name";
                return get(addEventState.fieldErrors, transformedKey);
            }
            return (
                get(addEventState.fieldErrors, transformedKey) ||
                get(
                    addEventState.fieldErrors,
                    `createEventRequests[0].${transformedKey}`,
                )
            );
        }

        if (addPromoterState.fieldErrors) {
            return get(addPromoterState.fieldErrors, transformedKey);
        }
    };

    const itemToLabel = (item: *) => {
        if (item) {
            const {
                uuid: itemUuid,
                name: itemName,
                internal: itemInternal,
                optionCode: itemOptCode,
                label,
            } = item;
            if (itemUuid) {
                const displayName =
                    itemName && itemOptCode
                        ? `${itemName} - ${itemOptCode}`
                        : `${itemName || itemOptCode}`;
                return (
                    displayName + (itemInternal ? ` ( ${itemInternal} )` : "")
                );
            }
            return label || itemName;
        }
        return "";
    };

    /**
     * Submit handler
     */
    const handleSubmit = (event: *) => {
        event.preventDefault();
        onSubmit(
            {
                assignParking,
                end,
                name,
                promoterUuid,
                start,
                status,
                venueUuid,
            },
            internal,
            seriesName,
            serieUuid,
            optionCode,
            category,
        );
    };

    /**
     * On mount: load venues, organisers, event details
     */
    useEffect(() => {
        loadVenues();
        if (loadOrganisers) {
            loadOrganisers();
        }
        if (loadCategories) loadCategories(uuid);
        if (loadEventDetails && uuid) {
            loadEventDetails(uuid, true);
        }
    }, []);

    /**
     * When venues data arrives, resolve venueUuid from route params
     */
    useEffect(() => {
        if (venues.data) {
            const paramDate = match.params.date;
            const venueCodeParam = paramDate ? paramDate.slice(-2) : "AS";
            const venue =
                !venues.loading &&
                venues.data &&
                venues.data.find(v => v.code === venueCodeParam);
            if (venue) {
                setVenueUuid(venue.uuid);
            }
        }
    }, [venues.data]);

    /**
     * Sync state from eventDetails when it changes
     */
    useEffect(() => {
        const data = eventDetails.data;
        if (data && !isEqual(prevEventDetailsDataRef.current, data)) {
            prevEventDetailsDataRef.current = data;
            //$FlowFixMe[prop-missing]
            setPromoterUuid(data.firstPromoterUuid);
            //$FlowFixMe[prop-missing]
            setInternal(data.internal);
            setSerieUuid(data.uuid);
            setSeriesName(data.name);
            setOptionCode(data.optionCode);
            setShowAlert(!data.name);
            setCategory(data.categoryUuid || null);
        }
    }, [eventDetails.data]);

    return (
        <Dialog open className={styles.element}>
            <form onSubmit={handleSubmit}>
                <DialogTitle disableTypography className={styles.title}>
                    <Typography variant="h2" color="inherit">
                        {translate("Add Event")}
                    </Typography>
                    <Typography color="inherit" className={styles.subTitle}>
                        {translate("Add an event to the calender:")}
                    </Typography>
                </DialogTitle>

                {eventDetails.loading ? (
                    <DialogContent className={styles.loading}>
                        <CircularProgress className={styles.spinner} />
                    </DialogContent>
                ) : (
                    <>
                        <DialogContent className={styles.purpleContent}>
                            <Grid container spacing={3}>
                                {showAlert && (
                                    <Grid item xs={12}>
                                        <Alert
                                            id="add-event-modal-series-name-alert"
                                            severity="error"
                                        >
                                            {translate("Series Name Alert")}
                                        </Alert>
                                    </Grid>
                                )}
                                <Grid item xs={6}>
                                    <AsyncFilteredSelect
                                        variant={"white"}
                                        id={`${id}-serie`}
                                        disabled={disabled}
                                        error={!!getError("serie")}
                                        helperText={getError("serie")}
                                        label={translate("Event series")}
                                        asyncState={events}
                                        lookup={findEvents}
                                        currentItem={
                                            (uuid && eventDetailsData) || ""
                                        }
                                        onSelect={serie =>
                                            serie.uuid
                                                ? (() => {
                                                      setSerieUuid(serie.uuid);
                                                      setSeriesName(serie.name);
                                                      setShowAlert(!serie.name);
                                                      setInternal(
                                                          serie.internal,
                                                      );
                                                      setPromoterUuid(
                                                          serie.firstPromoterUuid,
                                                      );
                                                      setOptionCode(
                                                          serie.optionCode,
                                                      );
                                                      setCategory(
                                                          serie.categoryUuid,
                                                      );
                                                  })()
                                                : (() => {
                                                      setSerieUuid(null);
                                                      setSeriesName(serie.name);
                                                      setInternal("");
                                                      setPromoterUuid("");
                                                      setOptionCode("");
                                                      setShowAlert(false);
                                                      setCategory(null);
                                                  })()
                                        }
                                        itemToString={item =>
                                            (item && item.name) || ""
                                        }
                                        itemToLabel={itemToLabel}
                                        createNewItem={newName =>
                                            newName && {
                                                uuid: null,
                                                name: newName,
                                                label: translate(
                                                    'Add "%{name}" as a new series',
                                                    { name: newName },
                                                ),
                                            }
                                        }
                                        fullWidth
                                        autoFocus
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        id="addshow-show-name"
                                        label={translate("Event Name")}
                                        onChange={({ target: { value } }) =>
                                            setName(value)
                                        }
                                        value={name || ""}
                                        error={!!getError("name")}
                                        helperText={getError("name")}
                                        disabled={disabled}
                                        fullWidth
                                        className={styles.whiteField}
                                        InputProps={{
                                            classes: {
                                                underline: styles.underline,
                                                disabled: styles.disabled,
                                                focused: styles.focused,
                                                error: styles.error,
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        id="addshow-event-code"
                                        label={translate("Option Code")}
                                        onChange={({ target: { value } }) =>
                                            setOptionCode(value)
                                        }
                                        value={optionCode || ""}
                                        error={!!getError("optionCode")}
                                        helperText={getError("optionCode")}
                                        disabled={
                                            disabled ||
                                            !!serieUuid ||
                                            !seriesName
                                        }
                                        fullWidth
                                        className={styles.whiteField}
                                        InputProps={{
                                            classes: {
                                                underline: styles.underline,
                                                disabled: styles.disabled,
                                                focused: styles.focused,
                                                error: styles.error,
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        id="addshow-memo-name"
                                        label={translate("Memo")}
                                        onChange={({ target: { value } }) =>
                                            setInternal(value)
                                        }
                                        value={internal || ""}
                                        error={!!getError("internal")}
                                        helperText={getError("internal")}
                                        disabled={
                                            disabled ||
                                            !!serieUuid ||
                                            !seriesName
                                        }
                                        fullWidth
                                        className={styles.whiteField}
                                        InputProps={{
                                            classes: {
                                                underline: styles.underline,
                                                disabled: styles.disabled,
                                                focused: styles.focused,
                                                error: styles.error,
                                            },
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={6}>
                                    <AsyncFilteredSelect
                                        variant={"white"}
                                        id={`${id}-promoter`}
                                        disabled={disabled}
                                        error={
                                            !!(
                                                addPromoterState.error ||
                                                (addPromoterState.fieldErrors &&
                                                    addPromoterState.fieldErrors
                                                        .name) ||
                                                getError("promoterUuid")
                                            )
                                        }
                                        helperText={
                                            (!!addPromoterState.error &&
                                                addPromoterState.error) ||
                                            (addPromoterState.fieldErrors &&
                                                addPromoterState.fieldErrors
                                                    .name) ||
                                            getError("promoterUuid")
                                        }
                                        currentItem={defaultPromoter || ""}
                                        label={translate("Organiser")}
                                        asyncState={organisers}
                                        onSelect={promoter =>
                                            setPromoterUuid(
                                                promoter.uuid || null,
                                            )
                                        }
                                        itemToString={item =>
                                            (item && item.name) || ""
                                        }
                                        itemToLabel={item =>
                                            (item &&
                                                (item.label || item.name)) ||
                                            ""
                                        }
                                        filter={(value, items) =>
                                            items
                                                .filter(item =>
                                                    item.name
                                                        .toLowerCase()
                                                        .includes(
                                                            value.toLowerCase(),
                                                        ),
                                                )
                                                .splice(0, 5)
                                        }
                                        fullWidth
                                    />
                                </Grid>

                                <Grid
                                    color="inherit"
                                    style={{
                                        opacity: "0.5",
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                    item
                                    xs={6}
                                    offset={6}
                                >
                                    <SelectList
                                        id="add-event-chooseCategory"
                                        onSelect={value => setCategory(value)}
                                        venueCode="SEA-BLUE"
                                        optionStatus={category}
                                        color="light"
                                        categories={categories}
                                        type="CATEGORY"
                                        error={!!getError("categoryUuid")}
                                        helperText={getError("categoryUuid")}
                                        label={translate("Category")}
                                    />
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogContent className={styles.whiteContent}>
                            <Grid container spacing={3}>
                                <Grid item sm={6}>
                                    <DateTimeField
                                        id="addshow-show-start"
                                        disabled={disabled}
                                        value={start}
                                        onChange={value => {
                                            if (value) {
                                                setStart(value);
                                                setEnd(
                                                    end.isAfter(value)
                                                        ? end
                                                        : value
                                                              .clone()
                                                              .add(2, "hours"),
                                                );
                                            }
                                        }}
                                        label={translate("Start")}
                                        error={!!getError("start")}
                                        helperText={getError("start")}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item sm={6}>
                                    <DateTimeField
                                        id="addshow-show-end"
                                        disabled={disabled}
                                        value={end}
                                        onChange={value => {
                                            if (value) setEnd(value);
                                        }}
                                        label={translate("End")}
                                        error={!!getError("end")}
                                        helperText={getError("end")}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <AsyncSelect
                                        id="addshow-show-venue"
                                        label={translate("Venue")}
                                        disabled={disabled}
                                        value={venueUuid || ""}
                                        loading={venues.loading}
                                        error={getError("venueUuid")}
                                        options={(venues.data || []).map(
                                            venue => [
                                                venue.uuid,
                                                <Grid
                                                    key={venue.uuid}
                                                    container
                                                    alignItems="center"
                                                    spacing={3}
                                                >
                                                    <Grid item xs>
                                                        <Typography>
                                                            {venue.name}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid
                                                        item
                                                        xs
                                                        justifyContent="flex-end"
                                                        container
                                                    >
                                                        <VenueBox
                                                            venue={venue}
                                                        />
                                                    </Grid>
                                                </Grid>,
                                            ],
                                        )}
                                        onChange={newVenueUuid => {
                                            setVenueUuid(newVenueUuid);
                                        }}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <SelectStatus
                                        id="addshow-status"
                                        error={getError("status")}
                                        label={translate("Status")}
                                        fullWidth
                                        venueCode={venueCode || "#298fc2"}
                                        optionsToSelect={["PUBLIC", "PRIVATE"]}
                                        onSelect={value => {
                                            setStatus(value.toString());
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    {addShowState.error && (
                                        <FormHelperText
                                            id={`${id}-helper-text`}
                                            error
                                        >
                                            {addShowState.error}
                                        </FormHelperText>
                                    )}
                                </Grid>
                            </Grid>
                        </DialogContent>
                    </>
                )}
                <DialogActions className={styles.actions}>
                    <Button
                        id="addshow-cancel"
                        onClick={onClose}
                        disabled={disabled}
                    >
                        {translate("Cancel")}
                    </Button>
                    <Button
                        id="addshow-submit"
                        type="submit"
                        disabled={disabled || showAlert}
                        color="primary"
                    >
                        {translate("Add event")}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default Add;
